import React from 'react';
import ArticleShell from './ArticleShell';
import { H2, H3, P, Callout, CheckList, BulletList } from './academyPrimitives';
import { getGuideBySlug } from '../../data/knowledgeAcademy';

const UAEVATTOGCGuide = () => {
  const guide = {
    ...getGuideBySlug('vat-transfer-business-uae-togc'),
    relatedService: { label: 'VAT Services', path: '/vat-services-uae' }
  };

  const toc = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'what-is-togc', label: 'What is a TOGC?' },
    { id: 'condition-1-business', label: 'Condition 1 — A Business, Not Just Assets' },
    { id: 'condition-2-taxable-person', label: 'Condition 2 — Buyer is a Taxable Person' },
    { id: 'condition-3-continuation', label: 'Condition 3 — Genuine Continuation' },
    { id: 'examples', label: 'Examples: What May and May Not Qualify' },
    { id: 'share-vs-asset', label: 'Share Sale vs Asset Sale' },
    { id: 'inventory-contracts', label: 'Inventory, Goodwill & Contracts' },
    { id: 'seller-duties', label: "Seller's Responsibilities" },
    { id: 'buyer-duties', label: "Buyer's Responsibilities" },
    { id: 'checklist', label: 'TOGC Documentation Checklist' },
    { id: 'mistakes', label: 'Common Mistakes' },
    { id: 'faq', label: 'Frequently Asked Questions' },
    { id: 'wpaa-help', label: 'How WPAA Can Support a Transfer' }
  ];

  return (
    <ArticleShell
      guide={guide}
      toc={toc}
      testId="ka-article-togc"
      ctaService="vat"
      heroImage={{
        src: 'https://customer-assets-jai6qajn.emergentagent.net/job_606545ce-2ce7-41b6-8e7f-1b01a327e549/artifacts/zpdzh7pk_Business%20Transfers%20UAE.png',
        alt: 'VAT on Business Transfers in the UAE — WPAA TOGC Guide',
        width: 1920,
        height: 965
      }}
    >
      <Callout variant="info" title="At a glance">
        A qualifying TOGC is <strong>not a supply</strong> for UAE VAT — no VAT is charged on the transfer
        itself. The treatment is <strong>compulsory</strong> when Article 7(2) conditions are met, not an
        election. Mislabelling an asset sale as a TOGC can trigger retrospective VAT.
      </Callout>

      <H2 id="introduction">Introduction</H2>
      <P>
        Buying or selling a business involves more than agreeing on a price. The parties must also determine
        whether the transaction is a normal sale of assets or a <strong>transfer of a business as a going
        concern</strong> (TOGC) for UAE VAT purposes.
      </P>
      <P>
        The distinction matters. A normal sale of business assets may be subject to VAT according to the
        treatment of each asset. A qualifying TOGC, however, is not regarded as a supply for VAT purposes —
        so no VAT is charged on the transfer itself.
      </P>
      <P>
        This is not an optional relief that the parties can choose in the sale agreement. If the legal
        conditions are met, TOGC treatment applies compulsorily. If the parties incorrectly treat an asset
        sale as a TOGC, VAT may become payable retrospectively.
      </P>

      <H2 id="what-is-togc">What Is a Transfer of a Business as a Going Concern?</H2>
      <P>
        <strong>Article 7(2) of the UAE VAT Decree-Law</strong> states that the transfer of a whole business,
        or an independent part of a business, from a person to a taxable person for the purpose of continuing
        that business is not considered a supply.
      </P>
      <P>
        In practical terms, the buyer must receive an operating business — or an independently operable part
        of one — rather than a collection of unrelated assets.
      </P>
      <P>The Federal Tax Authority (FTA) identifies three core conditions:</P>
      <CheckList items={[
        'The whole business or an independent part of it must be transferred.',
        'The transfer must be made to a taxable person.',
        'The buyer must genuinely intend to continue the transferred business.'
      ]} />
      <P>
        The facts at the completion date determine the treatment. Calling a transaction a &ldquo;TOGC&rdquo;
        in the agreement is useful evidence, but the label cannot make an ineligible transaction qualify.
      </P>

      <H2 id="condition-1-business">Condition 1: A Business, Not Merely Selected Assets</H2>
      <P>
        The transferred package must give the buyer possession of the business, or a part capable of
        operating separately. All goods and services necessary for its continued operation should pass to
        the buyer.
      </P>
      <P>Depending on the business, this may include:</P>
      <BulletList items={[
        'Goodwill and trading name',
        'Premises or the right to occupy them',
        'Licences and regulatory approvals, where transferable',
        'Machinery, equipment and inventory',
        'Employees',
        'Customer, supplier and other operating contracts',
        'Intellectual property and business systems',
        'Relevant liabilities'
      ]} />
      <P>
        Not every transaction requires every item on this list. The real question is whether the assets,
        rights and arrangements transferred together constitute a <strong>functioning business</strong>.
        Excluding an asset that is critical to continued operation may prevent TOGC treatment.
      </P>
      <P>
        The business must also be operational before and at the time of transfer. An activity that has not
        yet commenced, or a business that ceased operating before the transfer, would generally not
        qualify. A short temporary closure after completion may be acceptable when it is genuinely required
        to prepare the business to operate under its new ownership.
      </P>

      <H2 id="condition-2-taxable-person">Condition 2: The Buyer Must Be a Taxable Person at the Transfer Date</H2>
      <P>
        For TOGC treatment, the buyer must be a taxable person when the transfer takes place. According to
        the FTA&apos;s public clarification, this condition is met where, on the transfer date:
      </P>
      <BulletList items={[
        'The buyer is registered for VAT',
        'The buyer is required to register under the mandatory registration rules and has applied to the FTA, or',
        'The buyer has applied for voluntary registration and the FTA has accepted the application'
      ]} />
      <Callout variant="warning" title="Leaving registration until after closing is risky">
        An intention to consider registration after completion is <strong>not enough</strong>. The
        buyer&apos;s VAT position should be resolved and evidenced <strong>before</strong> the transaction
        closes.
      </Callout>
      <P>
        A frequently overlooked point is that the <strong>seller does not have to be VAT-registered</strong>
        for a TOGC to occur. The condition relates to the buyer&apos;s status. The seller&apos;s own historical
        registration and VAT obligations must nevertheless be reviewed separately.
      </P>

      <H2 id="condition-3-continuation">Condition 3: The Buyer Must Genuinely Continue the Business</H2>
      <P>
        The buyer must intend to carry on the <strong>same kind of business</strong> acquired. It may
        operate the acquired business separately or integrate it into an existing operation; either approach
        can qualify.
      </P>
      <P>
        The condition is unlikely to be met where the buyer intends to dispose of the assets, leave the
        business unused or fundamentally change their use. For example, acquiring an active property-leasing
        business and continuing to lease the property may qualify. Acquiring the same property with the
        intention of using it as the buyer&apos;s own headquarters would not represent continuation of the
        leasing business.
      </P>
      <P>
        There is no prescribed minimum period for which the buyer must operate the business, but the
        intention at completion must be genuine and supported by the surrounding facts.
      </P>

      <H2 id="examples">TOGC Examples: What May and May Not Qualify</H2>

      <H3>Example 1: Sale of an Operating Restaurant</H3>
      <P>
        A restaurant is transferred with its kitchen equipment, inventory, goodwill, employees, supplier
        arrangements and right to occupy the premises. The buyer is a taxable person and continues operating
        a restaurant.
      </P>
      <Callout variant="success" title="Likely qualifies">
        An operating business has been transferred and continued.
      </Callout>

      <H3>Example 2: Sale of Machinery Only</H3>
      <P>
        A manufacturer sells several machines but retains the premises, workforce, licences, customers and
        ongoing production activity.
      </P>
      <Callout variant="danger" title="Normally an asset sale, not a TOGC">
        The VAT treatment of the machinery must be determined under the ordinary rules.
      </Callout>

      <H3>Example 3: Transfer of an Independent Division</H3>
      <P>
        A company transfers a separately managed service division, including the staff, contracts, systems
        and assets needed for that division to operate independently. The buyer continues the same activity.
      </P>
      <Callout variant="success" title="May qualify">
        An independently operable part of a business, transferred intact, can qualify even though the seller
        retains its other divisions.
      </Callout>

      <H3>Example 4: Sale of a Pre-Operational Project</H3>
      <P>
        A business owner sells equipment, a business plan and preliminary approvals for an activity that has
        not started trading.
      </P>
      <Callout variant="danger" title="Generally does not qualify">
        There is no operational business for the buyer to continue.
      </Callout>

      <H2 id="share-vs-asset">Share Sale and Asset Sale Are Not the Same Transaction</H2>
      <P>
        A TOGC is a form of <strong>asset sale</strong>, not a share sale.
      </P>
      <P>
        In a share sale, ownership of the company changes, but the company continues to own its assets, hold
        its licences, employ its staff and remain responsible for its liabilities — including existing tax
        obligations. The VAT analysis of the transfer of shares is therefore different from the TOGC
        analysis.
      </P>
      <P>
        In an asset deal, ownership of the agreed assets and rights moves from the seller to the buyer. The
        parties must determine whether the transferred package is an operating business that satisfies
        Article 7(2), or simply a taxable or exempt sale of individual assets.
      </P>
      <P>
        This distinction should be established at the beginning of the transaction, as it affects tax due
        diligence, warranties, contract drafting and the allocation of risk.
      </P>

      <H2 id="inventory-contracts">What Happens to Inventory, Equipment, Goodwill and Contracts?</H2>
      <P>
        Where the transaction qualifies as a TOGC, the transfer as a whole is not a supply for VAT purposes.
        This treatment applies irrespective of the VAT treatment that might otherwise have applied to the
        underlying assets if sold separately.
      </P>
      <P>
        Accordingly, inventory, machinery, goodwill, contracts and other components transferred as part of
        the qualifying business are <strong>not separated merely to charge VAT asset by asset</strong>.
      </P>
      <P>
        Items sold outside the qualifying business package require separate analysis. For example, an asset
        retained from the main transaction and sold under a separate arrangement may be subject to the
        normal VAT rules.
      </P>

      <H2 id="seller-duties">Responsibilities of the Seller</H2>
      <P>Before treating a transaction as a TOGC, the seller should:</P>
      <CheckList items={[
        'Confirm that the transferred package constitutes the whole or an independently operable part of a business',
        "Verify and retain evidence of the buyer's status as a taxable person",
        'Obtain evidence that the buyer genuinely intends to continue the business',
        'Identify any excluded or separately sold assets and determine their VAT treatment',
        'Include clear VAT provisions in the sale agreement',
        'Review whether the sale creates a separate obligation to amend its FTA records or apply for VAT deregistration'
      ]} />
      <Callout variant="warning" title="Historical liabilities stay with the seller">
        TOGC treatment does not remove the seller&apos;s VAT liabilities from the period in which it owned
        and operated the business. Historical errors — such as a failure to register or account for VAT —
        remain matters for the seller even after the business is transferred.
      </Callout>

      <H2 id="buyer-duties">Responsibilities of the Buyer</H2>
      <P>The buyer should:</P>
      <CheckList items={[
        'Establish its VAT registration position before the completion date',
        'Assess whether it is receiving everything necessary to continue the business',
        'Document its genuine intention and practical plan to continue the activity',
        'Conduct tax due diligence appropriate to the deal structure',
        'Ensure that the contract deals with the consequences if the FTA later challenges the TOGC treatment',
        'Analyse separately any assets or services acquired outside the business transfer'
      ]} />
      <P>
        The buyer should not rely only on the seller&apos;s description of the transaction. The legal
        agreement, operational handover and post-completion conduct should all support the same conclusion.
      </P>

      <H2 id="checklist">TOGC Documentation Checklist</H2>
      <P>
        There is no substitute for a transaction-specific review. A well-supported file would commonly
        include:
      </P>
      <CheckList items={[
        'The signed business or asset transfer agreement',
        'A detailed schedule of transferred and excluded assets, rights and liabilities',
        'Evidence that the business was operational immediately before completion',
        "Copies of the buyer's VAT registration or relevant registration application and FTA status",
        "A written statement of the buyer's intention to continue the business",
        'Evidence of transferred employees, premises, licences, goodwill and key contracts, as applicable',
        "The buyer's transition or continuity plan",
        'A written VAT analysis mapping the facts to each TOGC condition',
        'Contractual provisions allocating responsibility if VAT is later assessed'
      ]} />
      <P>
        The wording of the contract matters, but it must be <strong>consistent with what actually
        happens</strong>.
      </P>

      <H2 id="mistakes">Common Mistakes in UAE Business Transfers</H2>

      <H3>1. Assuming every business sale is outside the scope of VAT</H3>
      <P>
        A sale of selected assets is not automatically a TOGC. The transferred elements must amount to a
        functioning business or an independently operable part.
      </P>

      <H3>2. Treating TOGC as an optional election</H3>
      <P>
        Where the statutory conditions are met, the treatment is compulsory. The parties cannot choose to
        charge VAT simply because it appears commercially convenient.
      </P>

      <H3>3. Leaving the buyer&apos;s VAT registration until after completion</H3>
      <P>
        The buyer&apos;s taxable-person status must be established at the time of transfer. Waiting until
        after closing can jeopardise the intended treatment.
      </P>

      <H3>4. Relying only on a TOGC clause</H3>
      <P>
        A contractual declaration does not override the facts. The assets transferred, the operational
        status of the business and the buyer&apos;s genuine intention must all support TOGC treatment.
      </P>

      <H3>5. Ignoring assets outside the main deal</H3>
      <P>
        Assets or services supplied separately may have their own VAT consequences and should be reviewed
        individually.
      </P>

      <H3>6. Overlooking the risk of retrospective VAT</H3>
      <P>
        If a transaction is incorrectly treated as a TOGC, VAT may become due retrospectively. The agreement
        should address who bears any VAT, administrative penalties and related costs, with legal advice
        obtained on the appropriate drafting.
      </P>

      <H2 id="faq">Frequently Asked Questions</H2>

      <H3>Is a TOGC zero-rated?</H3>
      <P>
        No. A qualifying TOGC is <strong>not a supply</strong> for UAE VAT purposes. This is different from
        a zero-rated supply, which remains a taxable supply charged at 0%.
      </P>

      <H3>Must the seller be VAT-registered?</H3>
      <P>
        No. The FTA clarification states that the seller is not required to be registered for a TOGC to take
        place. The buyer, however, must be a taxable person at the transfer date.
      </P>

      <H3>Can part of a business qualify?</H3>
      <P>
        Yes, provided the part is independent and capable of separate operation with the transferred assets,
        rights and arrangements.
      </P>

      <H3>Can the buyer temporarily close the business after completion?</H3>
      <P>
        A short temporary closure may be acceptable when necessary to prepare the business for operation
        under the new owner. The intention to continue must still be genuine.
      </P>

      <H3>Does a share sale qualify as a TOGC?</H3>
      <P>
        No. The FTA describes TOGC as a type of asset sale, not a share sale. A share transaction requires
        its own VAT and tax due-diligence analysis.
      </P>

      <H3>What if the FTA later decides that the transfer did not qualify?</H3>
      <P>
        VAT may become retrospectively due. This is why the conditions, evidence, purchase-price wording and
        contractual allocation of tax risk should be settled before completion.
      </P>

      <H2 id="wpaa-help">How WPAA Can Support a Business Transfer</H2>
      <P>
        White Paper Accounts Auditing (WPAA) supports buyers, sellers and their legal advisers with the
        financial and UAE tax aspects of business transfers. Our assistance can include:
      </P>
      <CheckList items={[
        'TOGC eligibility assessment',
        'VAT and financial due diligence',
        'Review of the assets and liabilities included in the transaction',
        'Buyer and seller VAT-registration checks',
        'Review of VAT clauses and supporting schedules from a tax perspective',
        'Transaction accounting and purchase-price support',
        'Post-completion VAT, accounting and compliance assistance'
      ]} />
      <Callout variant="info" title="Early review pays off">
        An early review is far more effective than trying to correct the VAT treatment after the transaction
        has closed. Planning to buy, sell or restructure a business in the UAE? Contact WPAA for a
        transaction-specific VAT review before signing or completion.
      </Callout>

      <H2>Official References</H2>
      <BulletList items={[
        'Federal Decree-Law No. 8 of 2017 on Value Added Tax and its amendments — Article 7(2)',
        'FTA VAT Public Clarification VATP015 — Transfer of a Business as a Going Concern'
      ]} />
      <P className="text-xs text-gray-500 italic mt-6">Last reviewed: 1 October 2026.</P>

      <Callout variant="warning" title="Disclaimer">
        This article is intended for general information only and does not constitute tax, accounting or
        legal advice. TOGC treatment depends on the facts, transaction documents and circumstances at the
        transfer date. UAE legislation, regulatory guidance and administrative practice may change. Obtain
        transaction-specific professional advice before relying on a particular VAT treatment or completing
        a business transfer.
      </Callout>
    </ArticleShell>
  );
};

export default UAEVATTOGCGuide;
